import type { FunAddress } from '@funkit/api-base'

import { useFunkitIdentity } from '../../identity/IdentityContext'
import { useLighterDepositRecipientOverride } from './useLighterDepositRecipientOverride'
import type { DepositTargetConfig } from '~/flow/depositFlowConfig'

export interface UseDepositRecipientResult {
  /** Who ends up with the funds; `0x` when nothing names one. */
  recipient: FunAddress
  /** A Lighter deposit whose main-account index has not resolved. */
  isLighterWalletFallback: boolean
  isLighterDeposit: boolean
  /** The resolved L2 main-account index, when there is one. */
  lighterRecipientOverride: string | undefined
  isLookupFetching: boolean
  lookupStatus: string
}

/**
 * Who a deposit pays, resolved once.
 *
 * Mirrors web's `useCheckoutTransferInit` priority: a resolved Lighter L2
 * main-account index outranks an explicit `recipientAddr`, which outranks the
 * host's configured `customRecipient`, which outranks the wallet. Its own hook
 * so a caller can read the recipient *before* deciding whether to mint a
 * deposit address for it.
 */
export function useDepositRecipient({
  checkoutConfig,
  recipientAddr,
  udaTargetChain,
}: {
  checkoutConfig: DepositTargetConfig | undefined
  recipientAddr?: FunAddress
  /** Target chain of the resolved UDA params, which decides Lighter. */
  udaTargetChain: string | undefined
}): UseDepositRecipientResult {
  const { apiKey, walletAddress } = useFunkitIdentity()
  const {
    isLighterDeposit,
    recipientOverride: lighterRecipientOverride,
    isLookupFetching,
    lookupStatus,
  } = useLighterDepositRecipientOverride({
    apiKey,
    targetChain: udaTargetChain,
    walletAddress,
  })

  const recipient = ((): FunAddress => {
    if (isLighterDeposit && lighterRecipientOverride) {
      return lighterRecipientOverride as FunAddress
    }
    return (recipientAddr ||
      checkoutConfig?.customRecipient ||
      walletAddress ||
      '0x') as FunAddress
  })()

  // Lighter EOA generation targets the L2 main-account index — the backend
  // rejects a raw L1 wallet address. When the lookup is pending, failed, or
  // found no main account (and nothing else pins the destination), the caller
  // holds the request instead of sending one that 400s.
  const isLighterWalletFallback =
    isLighterDeposit &&
    !lighterRecipientOverride &&
    !recipientAddr &&
    !checkoutConfig?.customRecipient

  return {
    recipient,
    isLighterWalletFallback,
    isLighterDeposit,
    lighterRecipientOverride,
    isLookupFetching,
    lookupStatus,
  }
}
